import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckIcon, ExternalLinkIcon, HeartHandshakeIcon, UploadIcon } from 'lucide-react';
import {
  Button,
  CardHeader,
  EmptyState,
  ErrorNotice,
  FactList,
  Field,
  LoadingState,
  Modal,
  Panel,
  Pill,
  SelectInput,
  TextArea,
  TextInput,
  type PillTone,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  listEventAttachments,
  readEventCsr,
  saveEventCsr,
  uploadEventAttachment,
  type CsrAttribution,
  type EventAttachment,
  type EventCsrChanges,
  type EventCsrPlan,
} from '@/lib/events';
import { DonationItemsSection } from './event-donation-items';

/**
 * The CSR tab on an event: BCE's side of the plan the client sees on their
 * portal (§37–40), kept in one place.
 *
 * The plan lives on the client's portal workspace, so the tab needs the event
 * to resolve to a portal organization through the Salesforce join. Until it
 * does, the tab shows what Salesforce filed about the charity and says how to
 * link the client. Once linked, staff record the beneficiary, the donation
 * and its status, may set the transport side on the client's behalf, and add
 * what came back afterwards — thank-you, acknowledgement, photographs, the
 * tax letter — the same way the client can from their side.
 */

const DONATION_STATUS = {
  PLANNED: 'Planned',
  ASSEMBLED: 'Assembled',
  AWAITING_PICKUP: 'Awaiting pickup',
  COLLECTED: 'Collected',
  DELIVERED: 'Delivered',
  CONFIRMED: 'Confirmed by the charity',
} as const;

const DONATION_STATUS_TONES: Readonly<Record<string, PillTone>> = {
  PLANNED: 'neutral',
  ASSEMBLED: 'brand',
  AWAITING_PICKUP: 'warning',
  COLLECTED: 'brand',
  DELIVERED: 'success',
  CONFIRMED: 'success',
};

const TRANSPORT = {
  UNDECIDED: 'Not decided yet',
  CHARITY_COLLECTS: 'The charity collects',
  CLIENT_ARRANGES: 'The client arranges it',
} as const;

const DOCUMENT_ROLE_LABELS: Readonly<Record<string, string>> = {
  CSR_PHOTO: 'Photo',
  CSR_TAX_LETTER: 'Tax letter',
  CSR_ACKNOWLEDGEMENT: 'Acknowledgement',
};

function readableDate(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Added by Dana Reed (client) · Sep 5, 2026". */
function attributionLine(attribution: CsrAttribution | undefined): string | null {
  if (!attribution) return null;
  const side = attribution.side === 'BCE' ? 'BCE' : 'client';
  return `Added by ${attribution.name} (${side}) · ${readableDate(attribution.at)}`;
}

function addedBy(attachment: EventAttachment): string {
  const by = attachment.uploadedBy;
  if (by && typeof by !== 'string') return `${by.firstName ?? ''} ${by.lastName ?? ''}`.trim();
  if (attachment.uploadedByName) {
    return attachment.uploadedBySide === 'CLIENT'
      ? `${attachment.uploadedByName} (client)`
      : attachment.uploadedByName;
  }
  return '';
}

/** A text field's draft: what the plan holds, or empty. */
function draft(value: string | null | undefined): string {
  return value ?? '';
}

/** Only the fields whose trimmed draft differs from the plan. */
function changedText(
  plan: EventCsrPlan | null,
  drafts: Readonly<Record<string, string>>,
): Record<string, string> {
  const changes: Record<string, string> = {};
  for (const [field, value] of Object.entries(drafts)) {
    const current = draft((plan as Record<string, unknown> | null)?.[field] as string | null | undefined);
    if (value.trim() !== current) changes[field] = value.trim();
  }
  return changes;
}

export function CsrTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const csr = useQuery({
    queryKey: ['event-csr', eventId],
    queryFn: ({ signal }) => readEventCsr(eventId, signal),
  });
  const attachments = useQuery({
    queryKey: ['event-attachments', eventId],
    queryFn: ({ signal }) => listEventAttachments(eventId, signal),
  });

  const save = useMutation({
    mutationFn: (changes: EventCsrChanges) => saveEventCsr(eventId, changes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event-csr', eventId] });
      toast.success('Charity plan saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const upload = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: 'CSR_PHOTO' | 'CSR_TAX_LETTER' }) =>
      uploadEventAttachment(eventId, file, {
        documentType,
        viewableBy: ['internal', 'contractor', 'client'],
      }),
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({ queryKey: ['event-attachments', eventId] });
      toast.success(`${input.file.name} added for both sides`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const [dialog, setDialog] = useState<'beneficiary' | 'transport' | 'afterwards' | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  if (csr.isPending) return <LoadingState label="Loading the charity plan…" />;
  if (csr.isError) return <ErrorNotice title="Charity plan unavailable" message={errorMessage(csr.error)} />;

  const { organization, charity, plan } = csr.data;
  const csrDocuments = (attachments.data ?? []).filter((row) => row.tags?.includes('csr'));
  const photos = csrDocuments.filter(
    (row) => row.documentType === 'CSR_PHOTO' || (row.type ?? '').startsWith('image/'),
  );
  const taxLetter = csrDocuments.find((row) => row.documentType === 'CSR_TAX_LETTER') ?? null;

  const charityFacts = charity.flatMap((entry, index) => [
    { label: charity.length > 1 ? `Charity ${index + 1}` : 'Charity in Salesforce', value: entry.name },
    { label: 'Charity contact', value: [entry.charityRep, entry.deliveryContact].filter(Boolean).join(' · ') || null },
  ]);

  function open(which: 'beneficiary' | 'transport' | 'afterwards'): void {
    setDrafts({
      beneficiaryName: draft(plan?.beneficiaryName),
      cause: draft(plan?.cause),
      charityContactName: draft(plan?.charityContactName ?? charity[0]?.charityRep),
      charityContactEmail: draft(plan?.charityContactEmail),
      donationRequirements: draft(plan?.donationRequirements),
      communitiesSupported: draft(plan?.communitiesSupported),
      donationStatus: plan?.donationStatus ?? 'PLANNED',
      engagedMinutes: plan?.engagedMinutes ? String(plan.engagedMinutes) : '',
      transportResponsibility: plan?.transportResponsibility ?? 'UNDECIDED',
      transportPlan: draft(plan?.transportPlan),
      charityArrivalTime: draft(plan?.charityArrivalTime),
      vehicleAccessNotes: draft(plan?.vehicleAccessNotes),
      alternativeTransportNote: draft(plan?.alternativeTransportNote),
      thankYouMessage: draft(plan?.thankYouMessage),
      beneficiaryAcknowledgement: draft(plan?.beneficiaryAcknowledgement),
      documentationNote: draft(plan?.documentationNote),
    });
    setDialog(which);
  }
  const set = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setDrafts((previous) => ({ ...previous, [field]: event.target.value }));

  function saveBeneficiary(): void {
    const text = changedText(plan, {
      beneficiaryName: drafts['beneficiaryName'] ?? '',
      cause: drafts['cause'] ?? '',
      charityContactName: drafts['charityContactName'] ?? '',
      charityContactEmail: drafts['charityContactEmail'] ?? '',
      donationRequirements: drafts['donationRequirements'] ?? '',
      communitiesSupported: drafts['communitiesSupported'] ?? '',
    });
    const changes: EventCsrChanges = { ...text };
    if ((drafts['donationStatus'] ?? '') !== (plan?.donationStatus ?? 'PLANNED')) {
      changes.donationStatus = drafts['donationStatus'] ?? 'PLANNED';
    }
    const minutes = Number.parseInt(drafts['engagedMinutes'] ?? '', 10);
    if (Number.isFinite(minutes) && minutes > 0 && minutes !== (plan?.engagedMinutes ?? 0)) {
      changes.engagedMinutes = minutes;
    }
    // An empty email would fail the API's format check; leave it out.
    if (changes.charityContactEmail === '') delete changes.charityContactEmail;
    if (Object.keys(changes).length > 0) save.mutate(changes);
    setDialog(null);
  }

  function saveTransport(): void {
    const changes: EventCsrChanges = changedText(plan, {
      transportPlan: drafts['transportPlan'] ?? '',
      charityArrivalTime: drafts['charityArrivalTime'] ?? '',
      vehicleAccessNotes: drafts['vehicleAccessNotes'] ?? '',
      alternativeTransportNote: drafts['alternativeTransportNote'] ?? '',
    });
    if ((drafts['transportResponsibility'] ?? '') !== (plan?.transportResponsibility ?? 'UNDECIDED')) {
      changes.transportResponsibility = drafts['transportResponsibility'] ?? 'UNDECIDED';
    }
    if (Object.keys(changes).length > 0) save.mutate(changes);
    setDialog(null);
  }

  function saveAfterwards(): void {
    // Only what changed: an untouched field keeps whoever wrote it.
    const changes = changedText(plan, {
      thankYouMessage: drafts['thankYouMessage'] ?? '',
      beneficiaryAcknowledgement: drafts['beneficiaryAcknowledgement'] ?? '',
      documentationNote: drafts['documentationNote'] ?? '',
    });
    if (Object.keys(changes).length > 0) save.mutate(changes);
    setDialog(null);
  }

  function uploadEach(files: FileList | null, documentType: 'CSR_PHOTO' | 'CSR_TAX_LETTER'): void {
    for (const file of Array.from(files ?? [])) upload.mutate({ file, documentType });
  }

  if (organization === null) {
    return (
      <div className="mt-6">
        <Panel className="p-5">
          <CardHeader
            title="Giving back"
            description="The charity plan lives on the client's portal, so this event needs its client on the enterprise portal first."
          />
          {charityFacts.length > 0 ? <FactList columns={2} facts={charityFacts} /> : null}
          <div className="mt-4">
            <EmptyState
              icon={<HeartHandshakeIcon aria-hidden />}
              title="No portal plan yet"
              message="Link the client's Salesforce account on their organization's Settings tab and the plan, the transport side and what comes back afterwards can all be kept here."
              action={
                <Button to="/organizations" variant="secondary">
                  Open organizations
                </Button>
              }
            />
          </div>
        </Panel>
      </div>
    );
  }

  const busy = save.isPending;

  return (
    <div className="mt-6 space-y-6">
      <Panel className="p-5">
        <CardHeader
          title="Giving back"
          description={`BCE records the beneficiary and where the donation is up to. ${organization.name} sees this on their portal.`}
          aside={
            <Button variant="secondary" size="compact" onClick={() => open('beneficiary')} disabled={busy}>
              {plan ? 'Edit' : 'Start the plan'}
            </Button>
          }
        />
        {plan === null ? (
          <>
            {charityFacts.length > 0 ? <FactList columns={2} facts={charityFacts} /> : null}
            <p className="mt-3 text-portal-small text-portal-muted">
              Nothing recorded yet. Start the plan with the beneficiary and what the charity needs.
            </p>
          </>
        ) : (
          <>
            <FactList
              columns={2}
              showEmpty
              facts={[
                { label: 'Beneficiary', value: plan.beneficiaryName },
                { label: 'Cause', value: plan.cause },
                {
                  label: 'Charity contact',
                  value: [plan.charityContactName, plan.charityContactEmail].filter(Boolean).join(' · ') || null,
                },
                { label: 'Communities supported', value: plan.communitiesSupported },
                { label: 'What they need', value: plan.donationRequirements },
                {
                  label: 'Donation status',
                  value: (
                    <Pill tone={DONATION_STATUS_TONES[plan.donationStatus ?? 'PLANNED'] ?? 'neutral'}>
                      {DONATION_STATUS[(plan.donationStatus ?? 'PLANNED') as keyof typeof DONATION_STATUS] ??
                        plan.donationStatus}
                    </Pill>
                  ),
                },
                {
                  label: 'Time actively engaged',
                  value: plan.engagedMinutes ? `${plan.engagedMinutes} minutes` : null,
                },
              ]}
            />
          </>
        )}
      </Panel>

      <DonationItemsSection
        eventId={eventId}
        clientName={organization.name}
        items={plan?.donationItems ?? []}
        counts={plan?.donationCounts}
      />

      <Panel className="p-5">
        <CardHeader
          title="Getting it there"
          description="The client's side (§37): who moves the donation and how. They set this from their portal; edit it here on their behalf."
          aside={
            <Button variant="secondary" size="compact" onClick={() => open('transport')} disabled={busy}>
              Edit
            </Button>
          }
        />
        <FactList
          columns={2}
          showEmpty
          facts={[
            {
              label: 'Who gets it there',
              value: TRANSPORT[(plan?.transportResponsibility ?? 'UNDECIDED') as keyof typeof TRANSPORT] ?? null,
            },
            { label: 'Charity arrives', value: plan?.charityArrivalTime },
            { label: 'The plan', value: plan?.transportPlan },
            { label: 'Vehicle and dock access', value: plan?.vehicleAccessNotes },
            { label: "If the charity can't collect", value: plan?.alternativeTransportNote },
          ]}
        />
      </Panel>

      <Panel className="p-5">
        <CardHeader
          title="After the event"
          description="What the charity sent back. Either side can add it; each item says who did."
          aside={
            <Button variant="secondary" size="compact" onClick={() => open('afterwards')} disabled={busy}>
              Edit
            </Button>
          }
        />
        {plan?.thankYouMessage || plan?.beneficiaryAcknowledgement || plan?.documentationNote ? (
          <div className="space-y-4">
            {plan.thankYouMessage ? (
              <div>
                <blockquote className="border-l-2 border-brand pl-3 text-portal-small italic">
                  {plan.thankYouMessage}
                </blockquote>
                <span className="mt-1 block text-portal-micro text-portal-muted">
                  {attributionLine(plan.attributions?.['thankYouMessage']) ?? 'Thank-you from the charity'}
                </span>
              </div>
            ) : null}
            {plan.beneficiaryAcknowledgement ? (
              <div>
                <span className="block text-portal-tiny font-bold uppercase tracking-wide text-portal-muted">
                  Acknowledgement
                </span>
                <span className="block text-portal-small">{plan.beneficiaryAcknowledgement}</span>
                <span className="mt-1 block text-portal-micro text-portal-muted">
                  {attributionLine(plan.attributions?.['beneficiaryAcknowledgement'])}
                </span>
              </div>
            ) : null}
            {plan.documentationNote ? (
              <div>
                <span className="block text-portal-tiny font-bold uppercase tracking-wide text-portal-muted">
                  Documentation
                </span>
                <span className="block text-portal-small">{plan.documentationNote}</span>
                <span className="mt-1 block text-portal-micro text-portal-muted">
                  {attributionLine(plan.attributions?.['documentationNote'])}
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-portal-small text-portal-muted">Nothing back from the charity yet.</p>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-portal-line pt-4">
          <div>
            <span className="block text-portal-tiny font-bold uppercase tracking-wide text-portal-muted">
              Photographs
            </span>
            <span className="block text-portal-micro text-portal-muted">
              {photos.length === 0 ? 'None yet.' : `${photos.length} photo${photos.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <input
            id="csr-photo-input"
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            disabled={upload.isPending}
            onChange={(event) => {
              uploadEach(event.target.files, 'CSR_PHOTO');
              event.target.value = '';
            }}
          />
          <Button
            variant="secondary"
            size="compact"
            disabled={upload.isPending}
            onClick={() => document.getElementById('csr-photo-input')?.click()}
          >
            <UploadIcon aria-hidden className="size-4" />
            {upload.isPending ? 'Uploading…' : 'Add photos'}
          </Button>
        </div>
        {photos.length > 0 ? (
          <ul className="mt-2 divide-y divide-portal-line border-t border-portal-line">
            {photos.map((row) => (
              <li key={row._id} className="flex flex-wrap items-center gap-3 py-2">
                <span className="min-w-0 flex-1">
                  <Link to={`/attachments/${row._id}`} className="text-portal-small font-bold text-brand hover:underline">
                    {row.name}
                  </Link>
                  <span className="block text-portal-micro text-portal-muted">
                    {[addedBy(row), readableDate(row.createdAt)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <Button to={`/attachments/${row._id}`} variant="secondary" size="compact">
                  <ExternalLinkIcon aria-hidden className="size-4" />
                  Open
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-portal-line pt-4">
          <div className="min-w-0">
            <span className="block text-portal-tiny font-bold uppercase tracking-wide text-portal-muted">
              Tax documentation
            </span>
            {taxLetter ? (
              <span className="flex flex-wrap items-center gap-2 text-portal-small">
                <CheckIcon aria-hidden className="size-4 text-emerald-700" />
                Tax documentation received
                <span className="text-portal-micro text-portal-muted">
                  {[taxLetter.name, addedBy(taxLetter), readableDate(taxLetter.createdAt)].filter(Boolean).join(' · ')}
                </span>
              </span>
            ) : (
              <span className="block text-portal-micro text-portal-muted">Not received yet.</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {taxLetter ? (
              <Button to={`/attachments/${taxLetter._id}`} variant="secondary" size="compact">
                <ExternalLinkIcon aria-hidden className="size-4" />
                Open
              </Button>
            ) : null}
            <input
              id="csr-tax-letter-input"
              type="file"
              accept="application/pdf,image/*"
              className="sr-only"
              disabled={upload.isPending}
              onChange={(event) => {
                uploadEach(event.target.files, 'CSR_TAX_LETTER');
                event.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              size="compact"
              disabled={upload.isPending}
              onClick={() => document.getElementById('csr-tax-letter-input')?.click()}
            >
              <UploadIcon aria-hidden className="size-4" />
              {taxLetter ? 'Replace' : 'Add tax letter'}
            </Button>
          </div>
        </div>
        {csrDocuments.some((row) => !photos.includes(row) && row !== taxLetter) ? (
          <ul className="mt-2 divide-y divide-portal-line border-t border-portal-line">
            {csrDocuments
              .filter((row) => !photos.includes(row) && row !== taxLetter)
              .map((row) => (
                <li key={row._id} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <Link to={`/attachments/${row._id}`} className="text-portal-small font-bold text-brand hover:underline">
                        {row.name}
                      </Link>
                      <Pill tone="neutral">{DOCUMENT_ROLE_LABELS[row.documentType ?? ''] ?? 'Charity'}</Pill>
                    </span>
                    <span className="block text-portal-micro text-portal-muted">
                      {[addedBy(row), readableDate(row.createdAt)].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </li>
              ))}
          </ul>
        ) : null}
      </Panel>

      <Modal
        open={dialog === 'beneficiary'}
        title="The beneficiary and the donation"
        subtitle={`Recorded with the charity. ${organization.name} reads this on their CSR card and in their impact numbers.`}
        onClose={() => setDialog(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={saveBeneficiary} disabled={busy}>Save</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); saveBeneficiary(); }}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Beneficiary" htmlFor="csr-beneficiary">
              <TextInput id="csr-beneficiary" value={drafts['beneficiaryName'] ?? ''} onChange={set('beneficiaryName')} autoFocus placeholder="Boys & Girls Clubs of Northern New Jersey" />
            </Field>
            <Field label="Cause" htmlFor="csr-cause">
              <TextInput id="csr-cause" value={drafts['cause'] ?? ''} onChange={set('cause')} placeholder="Youth mobility" />
            </Field>
            <Field label="Charity contact" htmlFor="csr-contact">
              <TextInput id="csr-contact" value={drafts['charityContactName'] ?? ''} onChange={set('charityContactName')} />
            </Field>
            <Field label="Contact email" htmlFor="csr-contact-email" hint="Optional">
              <TextInput id="csr-contact-email" type="email" value={drafts['charityContactEmail'] ?? ''} onChange={set('charityContactEmail')} />
            </Field>
          </div>
          <Field label="What they need" htmlFor="csr-requirements" hint="Sizes, assembly, packaging — what the charity asked for">
            <TextArea id="csr-requirements" rows={3} value={drafts['donationRequirements'] ?? ''} onChange={set('donationRequirements')} />
          </Field>
          <Field label="Communities supported" htmlFor="csr-communities" hint="Optional">
            <TextInput id="csr-communities" value={drafts['communitiesSupported'] ?? ''} onChange={set('communitiesSupported')} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Donation status" htmlFor="csr-status">
              <SelectInput id="csr-status" value={drafts['donationStatus'] ?? 'PLANNED'} onChange={set('donationStatus')}>
                {Object.entries(DONATION_STATUS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Minutes actively engaged" htmlFor="csr-minutes" hint="Drives the client's charity hours">
              <TextInput id="csr-minutes" type="number" min={1} max={1440} value={drafts['engagedMinutes'] ?? ''} onChange={set('engagedMinutes')} placeholder="90" />
            </Field>
          </div>
        </form>
      </Modal>

      <Modal
        open={dialog === 'transport'}
        title="How the donation gets to the charity"
        subtitle="The client's side of the plan. Set it here when they have told you by phone or email."
        onClose={() => setDialog(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={saveTransport} disabled={busy}>Save</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); saveTransport(); }}>
          <Field label="Who gets it there" htmlFor="csr-responsibility">
            <SelectInput id="csr-responsibility" value={drafts['transportResponsibility'] ?? 'UNDECIDED'} onChange={set('transportResponsibility')} autoFocus>
              {Object.entries(TRANSPORT).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="The plan" htmlFor="csr-transport-plan" hint="Vehicle, who loads, where it goes">
            <TextArea id="csr-transport-plan" rows={3} value={drafts['transportPlan'] ?? ''} onChange={set('transportPlan')} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Charity arrives" htmlFor="csr-arrival" hint="Optional">
              <TextInput id="csr-arrival" value={drafts['charityArrivalTime'] ?? ''} onChange={set('charityArrivalTime')} placeholder="4:15 PM" />
            </Field>
          </div>
          <Field label="Vehicle and dock access" htmlFor="csr-access" hint="Which dock, its height, where to check in">
            <TextArea id="csr-access" rows={2} value={drafts['vehicleAccessNotes'] ?? ''} onChange={set('vehicleAccessNotes')} />
          </Field>
          <Field label="If the charity can't collect" htmlFor="csr-fallback" hint="What the client covers instead">
            <TextArea id="csr-fallback" rows={2} value={drafts['alternativeTransportNote'] ?? ''} onChange={set('alternativeTransportNote')} />
          </Field>
        </form>
      </Modal>

      <Modal
        open={dialog === 'afterwards'}
        title="What came back from the charity"
        subtitle="Their thank-you and acknowledgement, and a note on the paperwork. Each one says who wrote it."
        onClose={() => setDialog(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={saveAfterwards} disabled={busy}>Save</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); saveAfterwards(); }}>
          <Field label="Thank-you from the charity" htmlFor="csr-thank-you" hint="In their words">
            <TextArea id="csr-thank-you" rows={3} value={drafts['thankYouMessage'] ?? ''} onChange={set('thankYouMessage')} autoFocus />
          </Field>
          <Field label="Acknowledgement" htmlFor="csr-acknowledgement" hint="How and when the charity confirmed the donation arrived">
            <TextArea id="csr-acknowledgement" rows={2} value={drafts['beneficiaryAcknowledgement'] ?? ''} onChange={set('beneficiaryAcknowledgement')} />
          </Field>
          <Field label="Documentation note" htmlFor="csr-documentation" hint="Optional — what paperwork exists and where">
            <TextInput id="csr-documentation" value={drafts['documentationNote'] ?? ''} onChange={set('documentationNote')} />
          </Field>
        </form>
      </Modal>
    </div>
  );
}
